import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ButtonProps, Button } from "./Button";
import { ListProps, List } from "./List";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { IconProps, ImageWithAspectRatioProps } from "./types";

export const cardFeatures = tv({
  base: cn(
    "uipm:flex uipm:flex-col uipm:font-normal uipm:h-full",
    "uipm:bg-[var(--surface-surface-secondary)] uipm:hover:bg-[var(--surface-surface-accent-secondary)]",
    "uipm:rounded-none uipm:md:rounded-[var(--radius-lg)]"
  ),
  variants: {
    verticalPadding: {
      default: "uipm:py-8",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-8",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

export interface CardFeaturesProps
  extends Omit<ComponentProps<"div">, "title">,
    VariantProps<typeof cardFeatures> {
  icon?: ImageWithAspectRatioProps;
  title?: TitleProps;
  text?: SimpleRichTextProps;
  features?: ListProps["items"];
  button?: ButtonProps;
  verticalPadding?: "default" | "small" | "medium" | "large";
  horizontalPadding?: "default" | "small" | "medium" | "large";
}

export const CardFeatures: React.FC<CardFeaturesProps> = ({
  icon,
  title,
  text,
  features,
  button,
  verticalPadding = "default",
  horizontalPadding = "default",
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        cardFeatures({ verticalPadding, horizontalPadding }),
        className
      )}
      {...props}
    >
      {/* Content wrapper that grows */}
      <div className="uipm:flex uipm:flex-col uipm:flex-grow">
        {/* Part 1: Icon + Title */}
        {(icon || title) && (
          <div className="uipm:flex uipm:items-center uipm:gap-4">
            {icon && (
              <div className="uipm:w-12 uipm:h-12 uipm:flex-shrink-0">
                <ImageWithAspectRatio {...icon} sizes="48px" />
              </div>
            )}
            {title && (
              <div className="uipm:flex-1 uipm:self-center">
                <Title {...title} className={title.className} />
              </div>
            )}
          </div>
        )}

        {/* Part 2: Text */}
        {text && (
          <div className="uipm:mt-4">
            <div className="uipm:text-[var(--typography-colors-text-faded-dark)] uipm:hover:text-[var(--typography-colors-text-accent)]">
              {text.children ? (
                <div
                  className={cn(
                    "uipm:text-[length:var(--typography-sizes-body-medium)]",
                    "uipm:leading-[var(--typography-line-heights-body-medium)]"
                  )}
                >
                  {text.children}
                </div>
              ) : (
                <SimpleRichText
                  {...text}
                  className={cn(
                    "uipm:text-[length:var(--typography-sizes-body-medium)]",
                    "uipm:leading-[var(--typography-line-heights-body-medium)]",
                    text.className
                  )}
                />
              )}
            </div>
          </div>
        )}

        {/* Part 3: Features List */}
        {features && features.length > 0 && (
          <div className="uipm:mt-4 uipm:pt-8">
            <List items={features} />
          </div>
        )}
      </div>

      {/* Part 4: Button - Always at bottom */}
      {button && (
        <div className="uipm:mt-6 uipm:pt-8">
          <Button {...button} className={cn("uipm:w-full", button.className)} />
        </div>
      )}
    </div>
  );
};
